/**
 * Shared Google Maps wrapper (Maps JavaScript API via @vis.gl/react-google-maps).
 *
 * Drop-in replacement for the old Amazon Location wrapper: exposes the SAME
 * interface (default map + Marker/Popup/Source/Layer with react-map-gl-style
 * props, onLoad handing back an adapter with getCenter/flyTo/fitBounds), so
 * every consumer migrates by changing one import path.
 *
 * The browser key is injected at build time (deploy.sh reads
 * abon/google-maps/browser-key from Secrets Manager). It ships in the bundle —
 * that is safe ONLY because the key is HTTP-referrer-restricted in GCP.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AdvancedMarker,
  APIProvider,
  InfoWindow,
  Map as GMap,
  useMap,
} from '@vis.gl/react-google-maps';

const BROWSER_KEY = process.env.REACT_APP_GOOGLE_MAPS_BROWSER_KEY || '';
// Map ID is required for AdvancedMarker. DEMO_MAP_ID renders default styling;
// swap for a console-created Map ID when custom styling is wanted.
const MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID';

const STORAGE_KEY = 'map_style_pref';

function getInitialStyle() {
  try { return localStorage.getItem(STORAGE_KEY) || 'streets'; } catch { return 'streets'; }
}

/** Adapter over google.maps.Map matching the maplibre method shapes consumers use. */
function makeAdapter(map) {
  return {
    getCenter: () => {
      const c = map.getCenter();
      return c ? { lat: c.lat(), lng: c.lng() } : null;
    },
    flyTo: ({ center, zoom } = {}) => {
      if (Array.isArray(center)) map.panTo({ lat: center[1], lng: center[0] });
      if (typeof zoom === 'number') map.setZoom(zoom);
    },
    fitBounds: (bounds, opts = {}) => {
      if (!Array.isArray(bounds) || bounds.length !== 2) return;
      const [[minLng, minLat], [maxLng, maxLat]] = bounds;
      map.fitBounds(
        { south: minLat, west: minLng, north: maxLat, east: maxLng },
        opts.padding ?? 40,
      );
      if (typeof opts.maxZoom === 'number' && window.google?.maps?.event) {
        window.google.maps.event.addListenerOnce(map, 'idle', () => {
          if (map.getZoom() > opts.maxZoom) map.setZoom(opts.maxZoom);
        });
      }
    },
    getMap: () => map,
  };
}

function MapBridge({ onLoad, onMoveEnd }) {
  const map = useMap();
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!map || loadedRef.current) return undefined;
    loadedRef.current = true;
    if (onLoad) onLoad({ target: makeAdapter(map) });
    let idleListener = null;
    if (onMoveEnd && window.google?.maps?.event) {
      idleListener = window.google.maps.event.addListener(map, 'idle', () => onMoveEnd());
    }
    return () => {
      if (idleListener) idleListener.remove();
    };
  }, [map, onLoad, onMoveEnd]);

  return null;
}

export default function GoogleMap({
  latitude = 26.64,
  longitude = -81.87,
  zoom = 12,
  style,
  children,
  onClick,
  onLoad,
  onMoveEnd,
  id,
  showStyleToggle = true,
  streetViewControl = false,  // pegman — enable on property panels (FR parity)
  ...rest
}) {
  const [mapStyle, setMapStyle] = useState(getInitialStyle);

  const toggleStyle = useCallback(() => {
    setMapStyle((prev) => {
      const next = prev === 'streets' ? 'satellite' : 'streets';
      try { localStorage.setItem(STORAGE_KEY, next); } catch { /* noop */ }
      return next;
    });
  }, []);

  if (!BROWSER_KEY) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', minHeight: 400, borderRadius: 12, background: 'var(--bg-elev)', color: 'var(--text-muted)', ...style }}>
        Map unavailable — no map key configured for this build.
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: 400 }}>
      <APIProvider apiKey={BROWSER_KEY}>
        <GMap
          id={id}
          mapId={MAP_ID}
          defaultCenter={{ lat: latitude, lng: longitude }}
          defaultZoom={zoom}
          mapTypeId={mapStyle === 'satellite' ? 'hybrid' : 'roadmap'}
          gestureHandling="greedy"
          disableDefaultUI={false}
          mapTypeControl={false}
          streetViewControl={streetViewControl}
          fullscreenControl={false}
          style={{ width: '100%', height: '100%', minHeight: 400, borderRadius: 12, ...style }}
          onClick={onClick}
          {...rest}
        >
          <MapBridge onLoad={onLoad} onMoveEnd={onMoveEnd} />
          {children}
        </GMap>
      </APIProvider>

      {showStyleToggle ? (
        <button
          type="button"
          onClick={toggleStyle}
          title={mapStyle === 'streets' ? 'Switch to Satellite' : 'Switch to Streets'}
          style={{
            position: 'absolute', bottom: 12, left: 12, zIndex: 10,
            width: 64, height: 44, borderRadius: 8,
            border: '2px solid var(--bg-1)', boxShadow: 'var(--shadow-md, none)',
            cursor: 'pointer', padding: 0, overflow: 'hidden',
            background: mapStyle === 'streets' ? 'var(--color-success)' : 'var(--color-warning-bg)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <span style={{
            color: mapStyle === 'streets' ? 'var(--color-accent-contrast)' : 'var(--color-warning-text)',
            fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
          }}>
            {mapStyle === 'streets' ? 'Satellite' : 'Streets'}
          </span>
        </button>
      ) : null}
    </div>
  );
}

/* ── Marker: react-map-gl-style props over AdvancedMarker ─────────────────── */
export function Marker({ latitude, longitude, anchor, onClick, style, children, ...rest }) {
  const handleClick = useCallback(
    (e) => {
      if (!onClick) return;
      onClick({ originalEvent: e?.domEvent || { stopPropagation: () => {} } });
    },
    [onClick],
  );
  return (
    <AdvancedMarker
      position={{ lat: Number(latitude), lng: Number(longitude) }}
      onClick={onClick ? handleClick : undefined}
      style={style}
      {...rest}
    >
      {children}
    </AdvancedMarker>
  );
}

/* ── Popup: InfoWindow with react-map-gl-style props ──────────────────────── */
export function Popup({ latitude, longitude, onClose, children, closeButton, closeOnClick, anchor, offset, className, ...rest }) {
  return (
    <InfoWindow
      position={{ lat: Number(latitude), lng: Number(longitude) }}
      onCloseClick={onClose}
      headerDisabled={closeButton === false}
      className={className}
      {...rest}
    >
      {children}
    </InfoWindow>
  );
}

/* ── Source/Layer: GeoJSON lines via google.maps.Data ─────────────────────── */
const SourceContext = createContext(null);

export function Source({ id, type, data, children }) {
  const map = useMap();
  const paint = useMemo(() => {
    const layer = React.Children.toArray(children).find((c) => c?.props?.paint);
    return layer?.props?.paint || {};
  }, [children]);

  useEffect(() => {
    if (!map || !data || !window.google?.maps?.Data) return undefined;
    const layer = new window.google.maps.Data({ map });
    try {
      layer.addGeoJson(data.type === 'FeatureCollection' || data.type === 'Feature'
        ? data
        : { type: 'Feature', geometry: data, properties: {} });
    } catch { /* malformed geojson — render nothing */ }
    const accent = (typeof window !== 'undefined'
      ? getComputedStyle(document.documentElement).getPropertyValue('--accent').trim()
      : '') || undefined;
    layer.setStyle({
      strokeColor: paint['line-color'] || accent,
      strokeWeight: paint['line-width'] || 3,
      strokeOpacity: paint['line-opacity'] ?? 0.9,
      fillOpacity: 0,
      clickable: false,
    });
    return () => layer.setMap(null);
  }, [map, data, paint]);

  return <SourceContext.Provider value={id}>{null}</SourceContext.Provider>;
}

/** Layer is consumed by Source (paint props read from children); renders nothing. */
export function Layer() {
  useContext(SourceContext);
  return null;
}
